import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";

export default function FallWinterSignature({
  eyebrow     = "Bodios Yak Down · 2026",
  title       = "Fall / Winter Signature",
  description = "Lorem ipsum dolor sit amet consectetur. Et lectus quam rhoncus enim nec ultrices blandit. Habitasse ultrices aliquet nisi amet vestibulum id. Et massa diam pharetra sollicitudin ultrices penatibus fringilla sagittis. Ante nulla egestas tellus laoreet adipiscing neque leo egestas.",
  imageSrc    = "/fall_winter.png",
  imageAlt    = "Fall Winter Signature Collection",
  ctaLabel    = "Explore the collection",
  onCtaClick  = () => {},
}) {
  const [imgLoaded,      setImgLoaded]      = useState(false);
  const [headingVisible, setHeadingVisible] = useState(false);
  const [imageVisible,   setImageVisible]   = useState(false);
  const [ctaVisible,     setCtaVisible]     = useState(false);

  const headingRef = useRef(null);
  const imageRef   = useRef(null);
  const ctaRef     = useRef(null);

  useEffect(() => {
    const opts = { threshold: 0.12 };
    const makeObserver = (setter) =>
      new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) setter(true);
      }, opts);

    const observers = [
      [headingRef, setHeadingVisible],
      [imageRef,   setImageVisible],
      [ctaRef,     setCtaVisible],
    ].map(([ref, setter]) => {
      const obs = makeObserver(setter);
      if (ref.current) obs.observe(ref.current);
      return obs;
    });

    return () => observers.forEach((obs) => obs.disconnect());
  }, []);

  const fadeBase = "transition-all duration-700 ease-out";
  const fadeIn   = "opacity-100 translate-y-0";
  const fadeOut  = "opacity-0 translate-y-6";
  const lineBase = "w-px origin-top transition-transform duration-[1200ms] ease-[cubic-bezier(0.76,0,0.24,1)]";
  const lineIn   = "scale-y-100";
  const lineOut  = "scale-y-0";

  return (
    <section className="w-full flex flex-col items-center px-6 overflow-hidden">

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Jost:wght@200;300;400&display=swap');

        .fws-title {
          font-family: 'Githera Regular', sans-serif;
          font-weight: 400;
          font-style: normal;
          font-size: 36px;
          line-height: 100%;
          letter-spacing: 0;
          text-align: center;
        }

        .fws-body { font-family: 'roboto', sans-serif; }

        .fws-img-wrap { overflow: hidden; }
        .fws-img-wrap img {
          transition: transform 1s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        }

        .fws-cta-btn {
          font-family: 'roboto', sans-serif;
          font-weight: 300;
          font-size: 11px;
          letter-spacing: 0.25em;
          text-transform: uppercase;
          background: transparent;
          padding: 13px 40px;
          cursor: pointer;
          transition: background 0.3s ease, color 0.3s ease;
        }

        .delay-150 { transition-delay: 150ms; }
        .delay-300 { transition-delay: 300ms; }
        .delay-450 { transition-delay: 450ms; }
      `}</style>

      {/* ── Heading block ── */}
      <div ref={headingRef} className="w-full max-w-3xl text-center pt-20">

        {/* eyebrow */}
        <p className={`fws-body font-extralight text-[11px] tracking-[0.22em] uppercase text-[#9b8b7a] mb-5
                       ${fadeBase} ${headingVisible ? fadeIn : fadeOut}`}>
          {eyebrow}
        </p>

        {/* title */}
        <h2 className={`fws-title text-[#2c2520] m-0 mb-6 delay-150 uppercase
                        ${fadeBase} ${headingVisible ? fadeIn : fadeOut}`}>
          {title}
        </h2>

        {/* divider */}
        <div className={`w-10 h-px mx-auto mb-7 delay-300
                         ${fadeBase} ${headingVisible ? fadeIn : fadeOut}`} />

        {/* body copy */}
        <p className={`fws-body font-light text-[13.5px] leading-[1.9]
                        max-w-[100%] mx-auto tracking-[0.02em] delay-450
                        ${fadeBase} ${headingVisible ? fadeIn : fadeOut}`}>
          {description}
        </p>
      </div>

      {/* ── 214px vertical line ── */}
      <div className="flex flex-col items-center mt-8 md:mt-0">
        <div
          className={`${lineBase} bg-[#c4aa90] ${headingVisible ? lineIn : lineOut}`}
          style={{ height: "214px" }}
        />
        <div className={`w-1.5 h-1.5 rounded-full bg-[#c4aa90] -mt-px
                         ${fadeBase} ${headingVisible ? "opacity-100" : "opacity-0"}`} />
      </div>

      {/* ── Image 1100×1100 ── */}
      <div
        ref={imageRef}
        className={`w-full fws-img-wrap relative
                    ${fadeBase} ${imageVisible ? fadeIn : fadeOut}`}
        style={{ maxWidth: "1100px", aspectRatio: "1 / 1" }}
      >
        <img
          src={imageSrc}
          alt={imageAlt}
          onLoad={() => setImgLoaded(true)}
          className="w-full h-full object-cover object-top"
          style={{ opacity: imgLoaded ? 1 : 0, transition: "opacity 0.7s ease" }}
        />
      </div>

      {/* ── 150px vertical line ── */}
      <div className="flex flex-col items-center">
        <div
          className={`${lineBase} bg-[#c4aa90] ${imageVisible ? lineIn : lineOut}`}
          style={{ height: "150px" }}
        />
        <div className={`w-1.5 h-1.5 rounded-full bg-[#c4aa90] -mt-px
                         ${fadeBase} ${imageVisible ? "opacity-100" : "opacity-0"}`} />
      </div>

      {/* ── CTA ── */}
      <div
        ref={ctaRef}
        className={`flex flex-col items-center gap-5 pb-24
                    ${fadeBase} ${ctaVisible ? fadeIn : fadeOut}`}
      >
        <Link
          to="/category/women"
          className="hover:text-[#2c2520] transition-colors"
          style={{
            fontFamily: "'Githera Regular', sans-serif",
            fontWeight: 400,
            fontSize: '20px',
            lineHeight: '32px',
            letterSpacing: 0,
          }}
        >
          Discover more
        </Link>
      </div>

    </section>
  );
}